import {useEffect, useRef, useState} from "react";
import {SpeedOutlined, FactoryOutlined} from "@mui/icons-material";
import {Box, LinearProgress} from "@mui/material";
import HorizontalCenteredTabs from "../../../Components/Tabs/HorizontalCenteredTabs";
import ActuatorEditor from "../Editors/ActuatorEditor";
import MeterEditor from "../Editors/MeterEditor";
import ManufacturerEditor from "../Editors/ManufacturerEditor";
import ValveEditor from "../Editors/ValveEditor";
import {HardwareCatalogProvider} from "../Context/HardwareCatalogContext";
import {HardwareCatalogClient} from "../Service/HardwareCatalogClient";
import useHardwareCatalog from "../Hooks/UseHardwareCatalog";
import ActuatorValveCompatibilityEditor from "../Editors/ActuatorValveCompatibilityEditor";
import InsertLinkOutlinedIcon from '@mui/icons-material/InsertLinkOutlined';
import PredefinedInstallationEditor from "../Editors/PredefinedInstallationEditor";
import RegionEditor from "../Editors/RegionEditor";
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import ManageHistoryOutlinedIcon from '@mui/icons-material/ManageHistoryOutlined';
import AlgoParametersEditor from "../Editors/AlgoParametersEditor";
import SettingsIcon from '@mui/icons-material/Settings';
import {createLabelWithLineBreaks} from "../../../General/SharedMethods";
import {ACTUATOR_VALVE_COMPATIBILITY_LABEL, ACTUATORS_LABEL, ALGO_PARAMETERS_LABEL, MANUFACTURERS_LABEL, PREDEFINED_INSTALLATIONS_LABEL, REGIONS_LABEL, SELECTED_TAB, VALVES_LABEL} from "../Constants/constants";
import ActuatorIcon from "../../../Assets/Icons/ActuatorIcon";
import ValveIcon from "../../../Assets/Icons/ValveIcon";

const HardwareCatalogPage = () => {
    const {getAllEnums} = useHardwareCatalog();
    const [adcMeasurementTypeOptions, setAdcMeasurementTypeOptions] = useState([]);
    const [byteOrderOptions, setByteOrderOptions] = useState([]);
    const [communicationInterfaceTypeOptions, setCommunicationInterfaceTypeOptions] = useState([]);
    const [communicationProtocolOptions, setCommunicationProtocolOptions] = useState([]);
    const [connectionBoxTypeOptions, setConnectionBoxTypeOptions] = useState([]);
    const [controlUnitTypeOptions, setControlUnitTypeOptions] = useState([]);
    const [powerOptions, setPowerOptions] = useState([]);
    const [electricalInterfaceTypeOptions, setElectricalInterfaceTypeOptions] = useState([]);
    const [meterTypeOptions, setMeterTypeOptions] = useState([]);
    const [modbusEncodingOptions, setModbusEncodingOptions] = useState([]);
    const [relayOptions, setRelayOptions] = useState([]);
    const [valveTypeOptions, setValveTypeOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [enums, setEnums] = useState([]);
    const booleanOptions = [{label: 'Yes', value: 'true'}, {label: 'No', value: 'false'}]
    const [selectedTab, setSelectedTab] = useState(() => parseInt(sessionStorage.getItem(SELECTED_TAB)) || 0);

    useEffect(() => {
        sessionStorage.setItem(SELECTED_TAB, selectedTab.toString());
    }, [selectedTab])

    useEffect(() => {
        getAllEnums().then((res) => {
            let enumsData = res.data;
            enumsData.booleans = {
                "Yes": "true",
                "No": "false"
            };
            setEnums(enumsData);
            setAdcMeasurementTypeOptions(transformEnum(enumsData.adcMeasurementType));
            setCommunicationInterfaceTypeOptions(transformEnum(enumsData.communicationInterfaceType));
            setCommunicationProtocolOptions(transformEnum(enumsData.communicationProtocol));
            setConnectionBoxTypeOptions(transformEnum(enumsData.connectionBoxType));
            setControlUnitTypeOptions(transformEnum(enumsData.controlUnitType));
            setElectricalInterfaceTypeOptions(transformEnum(enumsData.electricalInterfaceType));
            setMeterTypeOptions(transformEnum(enumsData.meterType));
            setPowerOptions(transformEnum(enumsData.powerSupply));
            setRelayOptions(transformEnum(enumsData.relay));
            setValveTypeOptions(transformEnum(enumsData.valveType));
            setIsLoading(false);

            function transformEnum(enumData) {
                let result = [];
                Object.keys(enumData).forEach(category => {
                    result.push({label: category, value: enumData[category]});
                });
                return result;
            }
        }).catch(error => {
            setIsLoading(false);
            console.error(error);
        });
    }, []);

    const tabs = [
        {
            label: <Box sx={{marginTop: "0.16rem"}}>Meters</Box>,
            icon: <SpeedOutlined/>,
            content: <MeterEditor
                modbusEncodingOptions={modbusEncodingOptions}
                electricalInterfaceTypeOptions={electricalInterfaceTypeOptions}
                byteOrderOptions={byteOrderOptions}
                adcMeasurementTypeOptions={adcMeasurementTypeOptions}
                meterTypeOptions={meterTypeOptions}
                communicationInterfaceTypeOptions={communicationInterfaceTypeOptions}
                controlUnitTypeOptions={controlUnitTypeOptions}
                enums={enums}
                booleanOptions={booleanOptions}
            />
        },
        {
            icon: <ActuatorIcon/>,
            label: ACTUATORS_LABEL,
            content: <ActuatorEditor relayOptions={relayOptions} powerOptions={powerOptions} enums={enums} booleanOptions={booleanOptions}/>
        },
        {
            icon: <InsertLinkOutlinedIcon sx={{marginTop: '0.5rem'}}/>,
            label: createLabelWithLineBreaks(ACTUATOR_VALVE_COMPATIBILITY_LABEL),
            content: <ActuatorValveCompatibilityEditor enums={enums}/>
        },
        {
            label: VALVES_LABEL,
            icon: <ValveIcon sx={{marginBottom: '0.3rem'}}/>,
            content: <ValveEditor valveTypeOptions={valveTypeOptions} enums={enums}/>
        },
        {
            label: MANUFACTURERS_LABEL,
            icon: <FactoryOutlined/>,
            content: <ManufacturerEditor enums={enums}/>
        },
        {
            label: createLabelWithLineBreaks(PREDEFINED_INSTALLATIONS_LABEL),
            icon: <ManageHistoryOutlinedIcon sx={{marginTop: '0.5rem'}}/>,
            content: <PredefinedInstallationEditor enums={enums} powerOptions={powerOptions}/>
        },
        {
            label: ALGO_PARAMETERS_LABEL,
            icon: <SettingsIcon/>,
            content: <AlgoParametersEditor enums={enums}/>
        },
        {
            label: REGIONS_LABEL,
            icon: <PublicOutlinedIcon/>,
            content: <RegionEditor enums={enums}/>
        },
    ];


    return isLoading ? <LinearProgress/> : <HorizontalCenteredTabs tabs={tabs} selectedTab={selectedTab} setSelectedTab={setSelectedTab}/>

}

export const HardwareCatalogProviders = () => {
    const hardwareCatalogClientRef = useRef(new HardwareCatalogClient(`${process.env.REACT_APP_SUPPORT_DASHBOARD_HARDWARE_CATALOG_URL}/api/v1`));
    if (hardwareCatalogClientRef.current) {
        return <HardwareCatalogProvider hardwareCatalogClientRef={hardwareCatalogClientRef.current}>
            <HardwareCatalogPage/>
        </HardwareCatalogProvider>
    } else {
        return null
    }
};

export default HardwareCatalogProviders;
